<template>
  <div>
    <v-dialog
      v-model="dialog"
      max-width="800px"
      transition="dialog-transition"
    >
      <v-btn flat color="primary" slot="activator">Calculate A Lead Score</v-btn>
      <v-card>
        <v-stepper v-model="currentStep">
          <v-stepper-header>
            <template
              v-for="(category, i) in engineData.categories"
            >
              <v-stepper-step
                :complete="false"
                :step="i + 1"
                :key="`${i}-step`"
                editable>{{category.name}}</v-stepper-step>
                <v-divider :key="`${i}-divider`"></v-divider>
            </template>
            <v-stepper-step
              :step="numSteps + 1"
              editable>Results
            </v-stepper-step>
          </v-stepper-header>
          <v-container pa-0>
            <v-stepper-items>
              <v-stepper-content
                v-for="(category, i) in engineData.categories"
                :step="i + 1"
                :key="`${i}-category`"
              >
                <v-flex xs12 text-xs-left class="pl-4">
                    <h3 class="headline">{{category.name}}</h3>
                </v-flex>
                <v-flex
                  xs12
                  class="px-4"
                  v-for="(factor, j) in category.factors"
                  :key="`${j}-factor`"
                >
                  <v-subheader class="pl-0 text-xs-left" >{{factor.name}}</v-subheader>
                  <v-radio-group v-model="factor.value" class="mt-1">
                    <v-radio
                      v-for="(choice, k) in factor.choices"
                      :label="choice.label"
                      :key="k"
                      :value="choice.score"></v-radio>
                  </v-radio-group>
                </v-flex>
                <!-- Navigation -->
                <v-btn color="primary" v-if="i > 0" @click="currentStep--">Previous</v-btn>
                <v-btn color="primary" v-if="i < numSteps" @click="currentStep++">Next</v-btn>
                <v-btn color="success" v-if="i === numSteps" @click="onComplete()">Done!</v-btn>
              </v-stepper-content>
              <v-stepper-content :step="numSteps + 1" class="pa-0">
                <div v-if="!rawScore.value" class="ma-4">
                  <v-responsive color="primary lighten-2" class="elevation-3" height="100%">
                    <h1 class="pa-4">You must select at least one answer!</h1>
                  </v-responsive>
                </div>
                <div v-else class="mx-4 mt-4 mb-2">
                  <v-subheader class="pl-0 pb-0">Lead Score</v-subheader>
                  <v-responsive class="elevation-3 my-2" height="100%" :style="results.value.style">
                    <h1 class="my-3 display-1"><b>{{results.value.score}}</b></h1>
                    <p>{{results.value.message}}</p>
                  </v-responsive>
                  <v-subheader class="pl-0 pb-0">Completeness</v-subheader>
                  <v-responsive class="elevation-3 my-2" height="100%" :style="results.completeness.style">
                    <h1 class="my-3 display-1"><b>{{results.completeness.score}}%</b></h1>
                    <p>{{results.completeness.message}}</p>
                  </v-responsive>
                  <v-btn color="primary" large flat class="mt-2" @click="copyToClipBoard(results.value.score)">Copy Score</v-btn>
                  <v-btn color="primary" large flat class="mt-2" @click="dialog=false">Done</v-btn>
                </div>
              </v-stepper-content>
            </v-stepper-items>
          </v-container>
        </v-stepper>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar"
      color="primary"
      :timeout="6000"
      bottom
    >
     Score copied to clipboard!
     <v-btn
       color="white"
       flat
       fab
       @click="snackbar = false"
     >
       <v-icon>close</v-icon>
     </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import engineData from './scoringEngineTemplate'
export default {
  data () {
    return {
      dialog: false,
      snackbar: false,
      currentStep: 0,
      engineData: engineData
    }
  },
  computed: {
    numSteps () {
      return engineData.categories.length
    },
    results () {
      // Set formated scores
      const results = {
        value: { score: (this.rawScore.value * 100).toFixed(0) },
        completeness: { score: (this.rawScore.completeness * 100).toFixed(0) }
      }
      // Set conditional colors
      const maxColorAngle = 90 // green ish
      let hslAngle = maxColorAngle * this.rawScore.value
      results.value.style = 'background-color: hsl(' + hslAngle + ', 100%, 50%)'
      hslAngle = maxColorAngle * this.rawScore.completeness
      results.completeness.style = 'background-color: hsl(' + hslAngle + ', 100%, 50%)'
      // Set conditional messages
      let score = results.value.score
      switch (true) {
        case (score > 80):
          results.value.message = 'This lead looks promising!'
          break
        case (score > 60):
          results.value.message = 'This lead looks alright'
          break
        case (score > 40):
          results.value.message = 'This lead isn\'t too great'
          break
        case (score > 20):
          results.value.message = 'This lead is not worth pursuing at this time'
          break
        default:
          results.value.message = 'You should not pursue this lead'
      }
      score = results.completeness.score
      switch (true) {
        case (score > 80):
          results.completeness.message = 'You\'ve got plenty of data!'
          break
        case (score > 60):
          results.completeness.message = 'You could use a little more data'
          break
        case (score > 40):
          results.completeness.message = 'You are running a short on information'
          break
        case (score > 20):
          results.completeness.message = 'There\'s not enough information to make a good judgment'
          break
        default:
          results.completeness.message = 'You need to talk to this lead more'
      }
      return results
    },
    rawScore () {
      let totalWeight = 0
      let weightedScore = 0
      let totalQuesetions = 0
      let answeredQuestions = 0
      engineData.categories.forEach((category) => {
        let categoryScore = 0
        let totalImportance = 0
        let hasValue = false
        category.factors.forEach((factor) => {
          totalQuesetions++
          if (factor.value !== undefined && factor.value !== null) {
            answeredQuestions++
            hasValue = true
            const maxValue = factor.maxValue || 5 // defualt to 0-5 scale
            categoryScore += (factor.value / maxValue) * factor.importance
            totalImportance += factor.importance
          }
        })
        if (hasValue) {
          totalWeight += category.weight
          weightedScore += (categoryScore / totalImportance) * category.weight
        }
      })
      return {
        value: weightedScore / totalWeight,
        completeness: answeredQuestions / totalQuesetions
      }
    }
  },
  methods: {
    copyToClipBoard (str) {
      const el = document.createElement('textarea')
      el.value = str
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      this.snackbar = true // notifiy user
    }
  }
}
</script>

<style lang="css">
</style>



// WEBPACK FOOTER //
// src/components/pages/Tools/LeadScoringEngine/LeadScoringEngine.vue